import {
  ArrowTrendingDownIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import { Skeleton } from '@onbeef/components/skeleton';
import {
  ClubSubscriptionStatus,
  Toast,
  formatCurrency,
} from '@onbeefapp/constants';
import cx from 'classnames';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { ClubPaymentIntervalToHuman } from '.';
import Stars from '../../../../../packages/assets/images/stars.png';
import WallPaper from '../../../../../packages/assets/images/wallpaper.png';
import Diamond from '../../assets/images/diamond.png';
import HeaderBar from '../../components/Headers/HeaderBar';
import { useSlug } from '../../hooks/useSlug';
import { useMerchantCache } from '../../queries/home';
import { Club } from '../../queries/home/types';
import { useAuthStore } from '../../stores/login';
import { useMerchantStore } from '../../stores/merchant';
import { useProductsStore } from '../../stores/products';

export const ClubPreview: React.FC = () => {
  const params = useSlug();
  const { isLoading } = useMerchantCache(params.slug);
  const merchant = useMerchantStore((state) => state.merchant);

  const navigate = useNavigate();

  const clubs = useMerchantStore((state) => state.clubs);

  const [clubName, setClubName] = React.useState('');
  const [clubID, setClubID] = React.useState<string>();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const free = clubs?.find((c) => c.price_cents === 0);
  const paid = clubs?.find((c) => c.price_cents > 0);

  const handleSubscription = () => {
    if (!clubID) {
      Toast.error('Seleciona um clube para assinar');
      return;
    }

    const haveAccount = isAuthenticated && user?.taxpayer_id_number != null;
    const alreadyHaveSubscription =
      haveAccount &&
      !!user.clubs.find(
        (club) => club.status === ClubSubscriptionStatus.ACTIVE,
      );

    if (alreadyHaveSubscription) {
      Toast.error(
        'Você já possui uma assinatura ativa, para assinar esse clube primeiro cancele a atual.',
      );
    } else {
      const club = clubs?.find((c) => c.id === clubID);
      navigate(`/club/${clubID}`, {
        state: { is_free: club?.price_cents === 0 },
      });
    }
  };

  React.useEffect(() => {
    if (clubs && clubs.length === 1) {
      setClubName(clubs[0]?.name || '');
      setClubID(clubs[0]?.id);
    }
  }, [clubs]);

  return (
    <div className="w-full h-[100vh]">
      <Helmet>
        <meta name="title" content={`Clubes ${merchant?.name}`} />
        <meta
          name="description"
          content={clubs?.map((club) => club.name).join(', ')}
        />
      </Helmet>
      <div className="bg-primary z-[-9] absolute w-full h-60 opacity-50" />
      <img
        src={Stars}
        alt="Imagem do clube"
        className="w-full h-60 z-[-8] object-cover absolute top-0 grayscale"
      />
      <img
        src={WallPaper}
        alt="Imagem do clube"
        className="w-full h-60 z-[-10] object-cover absolute top-0 grayscale"
      />

      <div className="w-full space-y-2">
        <HeaderBar className="p-4 !mb-16" colorButton="bg-white"></HeaderBar>
        <div className="flex flex-col space-y-4 items-center">
          <div className="absolute top-5 flex flex-col space-y-2">
            <img
              src={Diamond}
              alt="Imagem do clube"
              className="h-14 w-14 z-[-8] self-center"
            />
            <div className="text-lg font-medium text-contrastText">
              Clube de benefícios
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-between w-full h-[calc(100%-124px)] gap-4 p-4 rounded-t-2xl bg-white overflow-y-auto">
        <div className="w-full flex flex-col gap-4">
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-3 rounded-lg flex items-center gap-3">
            <Skeleton isLoading={isLoading} className="w-full h-10 rounded-lg">
              <img
                src={merchant?.logo}
                alt="Logotipo"
                className="h-16 rounded-full"
              />
              <div className="flex flex-col">
                <span className="font-medium text-gray-800">
                  Economize em todas as compras!
                </span>
                <span className="text-xs text-gray-600">
                  Assine e ganhe descontos exclusivos em produtos selecionados
                </span>
              </div>
            </Skeleton>
          </div>

          <div className="w-full flex flex-col gap-4">
            <Skeleton
              isLoading={isLoading}
              className="w-full h-[50vh] rounded-lg"
            >
              <ClubCard
                club={paid}
                value={clubName}
                setter={setClubName}
                setClubID={setClubID}
              />
              <ClubCard
                club={free}
                value={clubName}
                setter={setClubName}
                setClubID={setClubID}
              />
            </Skeleton>
          </div>
        </div>

        <Skeleton isLoading={isLoading} className="w-full h-10 rounded-lg">
          <button
            type="button"
            onClick={() => handleSubscription()}
            className="bg-primary text-contrastText rounded-lg w-full p-2 mt-2 mb-4"
          >
            Quero participar
          </button>
        </Skeleton>
      </div>
    </div>
  );
};

interface ClubCardProps {
  club?: Club;
  value?: string;
  setter: (clubName: string) => void;
  setClubID: (clubID: string) => void;
}

const ClubCard: React.FC<ClubCardProps> = ({
  club,
  value: _,
  setter,
  setClubID,
}) => {
  const categoriesAndProducts = useProductsStore(
    (state) => state.categoriesAndProducts,
  );

  // const isSelected = club?.name === value;

  const calculateDiscount = (original: number, discounted: number) => {
    return Math.round(((original - discounted) / original) * 100);
  };

  const products = React.useMemo(
    () => Object.values(categoriesAndProducts).flat(),
    [categoriesAndProducts],
  );

  return (
    club && (
      <div className="w-full relative">
        {/* {isSelected && (
          <CheckCircleIcon className="w-10 h-10 text-primary absolute right-[-10px] top-[-10px] transition-all duration-300" />
        )} */}
        <button
          type="button"
          onClick={() => {
            setter(club.name);
            setClubID(club.id);
          }}
          className={cx(
            'w-full border-2 p-6 flex text-start flex-col gap-4 rounded-lg transition-all duration-300',
            // isSelected ? 'border-primary' : 'border-gray-200'
          )}
        >
          <div className="w-full flex justify-between items-center">
            <span className="font-medium text-lg">{club.name}</span>
            <div className="flex items-center bg-gray-100 px-3 py-1 rounded-full">
              <span className="font-medium text-primary">
                {club.price_cents === 0
                  ? 'Grátis'
                  : `${formatCurrency(club.price_cents / 100)} /${
                      ClubPaymentIntervalToHuman[club.interval]
                    }`}
              </span>
            </div>
          </div>

          <ul className="flex flex-col gap-1 text-gray-600 font-medium">
            {club.messages?.map((message, index) => (
              <li key={index} className="flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-primary flex-shrink-0" />
                <span>{message}</span>
              </li>
            ))}
          </ul>

          {club.products && club.products.length > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-2 mb-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m8.99 14.993 6-6m6 3.001c0 1.268-.63 2.39-1.593 3.069a3.746 3.746 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043 3.745 3.745 0 0 1-3.068 1.593c-1.268 0-2.39-.63-3.068-1.593a3.745 3.745 0 0 1-3.296-1.043 3.746 3.746 0 0 1-1.043-3.297 3.746 3.746 0 0 1-1.593-3.068c0-1.268.63-2.39 1.593-3.068a3.746 3.746 0 0 1 1.043-3.297 3.745 3.745 0 0 1 3.296-1.042 3.745 3.745 0 0 1 3.068-1.594c1.268 0 2.39.63 3.068 1.593a3.745 3.745 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.297 3.746 3.746 0 0 1 1.593 3.068ZM9.74 9.743h.008v.007H9.74v-.007Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm4.125 4.5h.008v.008h-.008v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                  />
                </svg>
                <span className="font-medium">Produtos com desconto:</span>
              </div>
              <div
                className="flex flex-col max-h-60 lg:max-h-40 space-y-3 bg-gray-50 p-3 rounded-lg overflow-y-auto"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cccccc transparent',
                }}
              >
                {club.products?.map((product, index) => {
                  const p = products.find((p) => p.id === product.id);
                  if (!p) return null;
                  const image = p.media?.[0]?.file_url;
                  return (
                    <div
                      key={`club-product-${index}`}
                      className="flex items-start gap-3 p-2 rounded-lg transition-colors"
                    >
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                        <img
                          src={image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex flex-col flex-grow">
                        <div className="flex justify-between items-start">
                          <span className="font-medium line-clamp-1">
                            {product.name}
                          </span>
                          <div className="flex items-center gap-1 bg-primary/10 px-2 py-0.5 rounded-full ml-1 flex-shrink-0">
                            <ArrowTrendingDownIcon className="w-3 h-3 text-primary" />
                            <span className="text-primary font-bold text-xs">
                              {calculateDiscount(
                                product.price,
                                product.club_price,
                              )}
                              % OFF
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mt-1">
                          <span className="line-through text-gray-500 text-xs">
                            {formatCurrency(product.price)}
                          </span>
                          <span className="text-primary font-bold">
                            {formatCurrency(product.club_price)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </button>
      </div>
    )
  );
};
